import React, { useState, useRef, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import { formatDate } from "../../utils/Format";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import pin from '../../assets/img/Map/pin-icon.png';
import searchpin from '../../assets/img/Map/search-icon.png';

const NOMINATIM_BASE = "https://map.savplus-btp.com/search.php";

const searchIcon = new L.Icon({
	iconUrl: searchpin,
	iconSize: [30, 30],
	iconAnchor: [15, 30],
	popupAnchor: [0, -30],
});

const defaultIcon = L.icon({
	iconUrl: pin,
	iconSize: [30, 30],
	iconAnchor: [15, 30],
	popupAnchor: [0, -30],
});

const FlyToLocation = ({ lat, lon }) => {
	const map = useMap();
	useEffect(() => {
		map.flyTo([lat, lon], 12);
	}, [lat, lon, map]);
	return null;
};

const ResizeOnSignal = ({ signal }) => {
	const map = useMap();
	useEffect(() => {
		const t = setTimeout(() => {
			map.invalidateSize();
		}, 0);
		return () => clearTimeout(t);
	}, [signal, map]);

	useEffect(() => {
		const onWinResize = () => map.invalidateSize();
		window.addEventListener("resize", onWinResize);
		return () => window.removeEventListener("resize", onWinResize);
	}, [map]);

	return null;
};

const geocodeCache = new window.Map();

const buildGeocodeQuery = (loc) => {
	const addr = String(loc?.Adresse1 || "").trim();
	const city = String(loc?.Ville || "").trim();
	const cp = String(loc?.CodePostal || "").trim();
	const country = String(loc?.Pays || "").trim();

	if (!addr || !city) return null;

	const q = `${addr}, ${cp} ${city} ${country}`.trim();
	return q.length >= 5 ? q : null;
};

const toNumber = (v) => {
	if (v == null) return null;
	const s = String(v).trim().replace(",", ".");
	if (!s) return null;
	const n = Number(s);
	return Number.isFinite(n) ? n : null;
};

const getLatLng = (loc) => {
	const lat = toNumber(loc?.Localisation?.latitude);
	const lon = toNumber(loc?.Localisation?.longitude);
	if (lat == null || lon == null) return null;
	if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
	return { lat, lon };
};


const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const DEFAULT_CENTER = [46.6031, 1.8883];

const Map = ({ locations = [], customIcon, resizeSignal, searchEnabled = true, zoom = 6 }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [suggestions, setSuggestions] = useState([]);
	const [selectedResult, setSelectedResult] = useState(null);
	const timeoutRef = useRef(null);

	const [resolvedLocations, setResolvedLocations] = useState([]);
	const [map, setMap] = useState(null);

	const normLocations = useMemo(() => Array.isArray(locations) ? locations : [], [locations]);

	useEffect(() => {
		let cancelled = false;

		const resolveAll = async () => {
			const out = [];

			for (const loc of normLocations) {
				const ll = getLatLng(loc);
				const hasLatLon = !!ll;


				if (hasLatLon) {
					out.push({
						...loc,
						Localisation: { latitude: ll.lat, longitude: ll.lon },
					});
					continue;
				}

				const q = buildGeocodeQuery(loc);
				if (!q) {
					out.push(loc);
					continue;
				}

				if (geocodeCache.has(q)) {
					const cached = geocodeCache.get(q);
					if (cached) {
						out.push({
							...loc,
							Localisation: {
								latitude: parseFloat(cached.lat),
								longitude: parseFloat(cached.lon),
							},
							_geocodedFrom: cached.display_name,
						});
					} else {
						out.push(loc);
					}
					continue;
				}

				try {
					const url = `${NOMINATIM_BASE}?q=${encodeURIComponent(q)}&format=json&limit=1`;
					const res = await fetch(url);
					const data = await res.json();

					const first = Array.isArray(data) && data.length > 0 ? data[0] : null;
					geocodeCache.set(q, first);

					if (first) {
						out.push({
							...loc,
							Localisation: {
								latitude: parseFloat(first.lat),
								longitude: parseFloat(first.lon),
							},
							_geocodedFrom: first.display_name,
						});
					} else {
						out.push(loc);
					}
				} catch (e) {
					console.error("Geocode error:", e);
					geocodeCache.set(q, null);
					out.push(loc);
				}

				// douceur pour l'API
				await sleep(150);
				if (cancelled) return;
			}

			if (!cancelled) setResolvedLocations(out);
		};

		resolveAll();
		return () => {
			cancelled = true;
		};
	}, [normLocations]);

	const handleInputChange = (e) => {
		const value = e.target.value;
		setSearchTerm(value);

		if (timeoutRef.current) clearTimeout(timeoutRef.current);

		timeoutRef.current = setTimeout(() => {
			if (value.length < 2) {
				setSuggestions([]);
				return;
			}

			fetch(`${NOMINATIM_BASE}?q=${encodeURIComponent(value)}&format=json`)
				.then(res => res.json())
				.then(data => setSuggestions(data))
				.catch(err => {
					console.error("Erreur Nominatim :", err);
					setSuggestions([]);
				});
		}, 300);
	};

	const handleSuggestionClick = (place) => {
		setSelectedResult({
			lat: parseFloat(place.lat),
			lon: parseFloat(place.lon),
			name: place.display_name,
		});
		setSearchTerm(place.display_name);
		setSuggestions([]);
	};

	if (customIcon == null) {
		customIcon = new L.Icon({
			iconUrl: pin,
			iconSize: [30, 30],
			iconAnchor: [15, 30],
			popupAnchor: [0, -30]
		});
	}

	const validPoints = useMemo(() => {
		return (resolvedLocations || []).filter(
			(l) => l?.Localisation?.latitude != null && l?.Localisation?.longitude != null
		);
	}, [resolvedLocations]);

	const prevCountRef = useRef(validPoints.length);

	// useEffect(() => {
	// 	console.log("Map: validPoints changed, count =", validPoints.length, map);
	// 	const count = validPoints.length;
	// 	console.log("Map: adjusting view for", count, "points");
	// 	if (count === 1) {
	// 		const { latitude, longitude } = validPoints[0].Localisation;
	// 		if (latitude != null && longitude != null) {
	// 			FlyToLocation({ lat: latitude, lon: longitude });
	// 		}
	// 	} else if (prevCountRef.current !== count) {
	// 		map.setView(DEFAULT_CENTER, zoom, { animate: true });
	// 	}

	// 	prevCountRef.current = count;
	// }, [map, zoom, validPoints]);
	// debugger;

	return (
		<div className="relative border-2 border-gray-300 hover:border-orange-400 rounded w-full h-full transition duration-300 ease-in-out">

			{searchEnabled && (
				<div className="top-4 left-4 z-[1000] absolute bg-white shadow-lg p-2 rounded w-[420px]">
					<input
						type="text"
						placeholder="Rechercher un lieu (ex: Nice, Paris)"
						className="p-2 border rounded w-full text-sm"
						value={searchTerm}
						onChange={handleInputChange}
					/>
					{suggestions.length > 0 && (
						<ul className="bg-white shadow-md mt-2 border border-gray-300 rounded max-h-60 overflow-y-auto text-sm">
							{suggestions.map((sugg, index) => (
								<li
									key={index}
									className="hover:bg-orange-100 p-2 cursor-pointer"
									onClick={() => handleSuggestionClick(sugg)}
								>
									{sugg.display_name}
								</li>
							))}
						</ul>
					)}
				</div>
			)}

			<MapContainer
				center={DEFAULT_CENTER}
				zoom={zoom}
				style={{ height: "100%", width: "100%" }}
				whenCreated={setMap}
			>
				<ResizeOnSignal signal={resizeSignal} />
				<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

				{resolvedLocations?.map((location) => {
					const ll = getLatLng(location);
					if (!ll) return null;

					return (
						<Marker
							key={location.id ?? `${location.Nom}-${ll.lat}-${ll.lon}`}
							position={[ll.lat, ll.lon]}
							icon={customIcon || defaultIcon}
						>
							<Popup>
								<h2><b>{location.Nom}</b></h2>
								{location._geocodedFrom && (
									<p className="text-gray-500 text-xs">
										(coordonnées suggérées : {location._geocodedFrom})
									</p>
								)}
								{location.Adresse1 && <li><b>Adresse:</b> {location.Adresse1}</li>}
								{location.declarationLibelle && <li><b>Déclaration:</b> {location.declarationLibelle}</li>}
								{location.rdvDate && <li><b>Date de RDV:</b> {formatDate(location.rdvDate)}</li>}
								{location.rdvHeure && <li><b>Heure de RDV:</b> {location.rdvHeure}</li>}
								{location.UUID_Declaration && (
									<Link to={`/declarations/${location.UUID_Declaration}`}>
										<i className="fa-arrow-right fa-solid"></i> Aller à la déclaration
									</Link>
								)}
							</Popup>
						</Marker>
					);
				})}


				{resolvedLocations.length === 1 && resolvedLocations[0]?.Localisation && (
					<FlyToLocation lat={resolvedLocations[0]?.Localisation?.latitude} lon={resolvedLocations[0]?.Localisation?.longitude} />
				)}

				{selectedResult && (
					<>
						<Marker position={[selectedResult.lat, selectedResult.lon]} icon={searchIcon}>
							<Popup>
								<h2><b>{selectedResult.name}</b></h2>
								<p>Résultat de recherche</p>
							</Popup>
						</Marker>
						<FlyToLocation lat={selectedResult.lat} lon={selectedResult.lon} />
					</>
				)}
			</MapContainer>
		</div>
	);
};

export default Map;
